import NoResults from '@common-assets/lottie/no-results.json';
import GroupHeader from '@common-components/tasks/group-header/GroupHeader';
import { BoardRowElement } from "@common-components/tasks/helpers/elements-factory/elementsFactory";
import { BoardRowType, BTdragElement } from "@common-models/task";
import { Box, ClickAwayListener, Popper, Stack } from "@mui/material";
import { getBlueticksThemeSelector, getViewFiltersLength } from "@common-reducers/BoardsSelectors";
import { hasBulkTasks } from "@common-reducers/BulkActionsReducer";
import { selectElementsList } from "@common-reducers/TaskSelectors";
import { taskActions } from "@common-reducers/TasksReducer";
import { updateTasksOrderAfterTaskDragEnd } from '@common-reducers/TaskThunks';
import { isExtensionContextOrWhatsappView } from "@common-services/detect-context";
import { useAppDispatch } from "@common-reducers/hooks/store.hook";
import ConditionalWrapper from "@common-components/conditional-wrapper/ConditionalWrapper";
import Lottie from "@/common/components/lazy-lottie/lazy-lottie";
import React, {
  DetailedHTMLProps,
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { debugLog } from "@common/utils/debug-logger";
import { DragDropContext, Draggable, DragStart, Droppable, DropResult, ResponderProvided } from "react-beautiful-dnd";
import { Virtuoso } from "react-virtuoso";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { BoardRow } from "../tasks/board-row/BoardRow";
import BulkActionsDialog from "./bulk-actions-dialog/bulk-actions-dialog";
import MoveToMenu from "./bulk-actions-dialog/move-to/move-to-menu";
import classes from "./chat-task-list.module.scss";
import { debounce } from 'lodash';
import BoardsLoader from './recents-boards/boards-loader';
import FpsTracker from './FpsTracker';
import { useShadowDOM } from '@/common/contexts/ShadowDOMContext';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { useIsMobile } from '../../hooks/use-is-mobile';
import MobileBoardView from './mobile-board/MobileBoardView';


// Virtuoso's resize observer can this error,
// which is caught by DnD and aborts dragging.
window.addEventListener("error", (e) => {
  if (
    e.message ===
    "ResizeObserver loop completed with undelivered notifications." ||
    e.message === "ResizeObserver loop limit exceeded"
  ) {
    e.stopImmediatePropagation();
  }
});

type Props = {
  isDisplayNewGroupButton?: boolean;
  isFromCampaign?: boolean;
  height?: Number;
  /** The board's data is still being fetched. When set (campaign audience
   *  editor), the list renders an in-place row skeleton — encapsulated here so
   *  it sits exactly where the real rows will, instead of a separate placeholder
   *  positioned by the parent. */
  isLoading?: boolean;
}


const buildAnchorSelector = (id?: string): string | null => {
  if (!id) {
    return null;
  }
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return `#${CSS.escape(id)}`;
  }
  if (/^[A-Za-z0-9\-_]+$/.test(id)) {
    return `#${id}`;
  }
  return null;
};

function ChatTaskList({ isDisplayNewGroupButton = true, isFromCampaign = false, height, isLoading = false }: Props) {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const [stickyHeader, setStickyHeader] = useState<BoardRowElement | null>(null);
  const groupHeaderProvidedRef = useRef<any>();
  const theme = useAppSelector((state) => getBlueticksThemeSelector(state));
  const elementList = useAppSelector(selectElementsList);
  const textFilter = useAppSelector((state) => state.TasksReducer.tasksFilter.text);
  const viewFilterLength = useAppSelector((state) => getViewFiltersLength(state));
  const bulkActionMoveToId = useAppSelector((state) => state.BulkActionsReducer.bulkActionMoveToId);
  const direction = useAppSelector((state) => state.UserReducer.direction);
  const isMoveTo = useAppSelector((state) => state.BulkActionsReducer.isMoveTo);

  const { shadowRoot } = useShadowDOM();
  const portalContainer = usePortalContainer();

  const menuAnchorEl = useMemo<HTMLElement | null>(() => {
    if (!bulkActionMoveToId) {
      return null;
    }

    const anchorId = `bulk-actions-menu-${bulkActionMoveToId}`;
    const selector = buildAnchorSelector(anchorId);

    const candidates: Array<Document | ShadowRoot | HTMLElement> = [];

    if (shadowRoot) {
      candidates.push(shadowRoot);
      if (shadowRoot.host instanceof HTMLElement) {
        candidates.push(shadowRoot.host);
      }
    }

    if (portalContainer) {
      if (portalContainer instanceof ShadowRoot) {
        candidates.push(portalContainer);
        if (portalContainer.host instanceof HTMLElement) {
          candidates.push(portalContainer.host);
        }
      } else {
        candidates.push(portalContainer);
      }
    }

    candidates.push(document);

    for (const root of candidates) {
      let found: Element | null = null;

      if ('getElementById' in root && typeof root.getElementById === 'function') {
        found = root.getElementById(anchorId);
      }

      if (!found && selector && 'querySelector' in root && typeof root.querySelector === 'function') {
        found = root.querySelector(selector);
      }

      if (found instanceof HTMLElement) {
        return found;
      }
    }

    return null;
  }, [bulkActionMoveToId, portalContainer, shadowRoot, isMoveTo]);


  const isTasksLoaded = useAppSelector((state) => state.DBTasksReducer.itemsLoaded);
  // const isTaskColumnsLoaded = useAppSelector((state) => state.DBTaskColumnReducer.itemsLoaded);
  // const isTaskGroupsLoaded = useAppSelector((state) => state.DBTaskgroupsReducer.itemsLoaded);
  // const isTaskColumnOptionsLoaded = useAppSelector((state) => state.DBTaskColumnOptionReducer.itemsLoaded);
  // const isBoardItemsLoaded = useMemo(() => {
  //   return isTaskColumnsLoaded && isTaskGroupsLoaded && isTaskColumnOptionsLoaded && isTasksLoaded
  // }, [])

  const scrollContainerRef = useRef(null);

  const params = new URLSearchParams(window.location.search);
  const paramsTaskId: string | undefined = params.get('taskId') ?? undefined;






  // Stable across renders: recreating these debouncers every render (the prior
  // behavior) allocated fresh functions per render AND reset the debounce window,
  // so coalescing was unreliable. Memoize on `dispatch` (stable) and cancel any
  // pending trailing dispatch on unmount.
  const debouncedStopScrollDispatch = useMemo(
    () => debounce((isScrollingNow) => {
      dispatch(taskActions.setTaskViewIsScrollingEnd(isScrollingNow));
    }, 500),
    [dispatch]
  );

  const debouncedStartScrollDispatch = useMemo(
    () => debounce((isScrollingNow) => {
      if (isScrollingNow) {
        dispatch(taskActions.setTaskViewIsScrollingStart(true));
      }
    }, 2000),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      debouncedStopScrollDispatch.cancel();
      debouncedStartScrollDispatch.cancel();
    };
  }, [debouncedStopScrollDispatch, debouncedStartScrollDispatch]);

  // const setScrollVelocity = (scrollTop) => {
  //   const currentTimestamp = Date.now();
  //   const timeElapsed = currentTimestamp - lastTimestamp.current;

  //   if (timeElapsed > 0) {
  //     const scrollDifference = scrollTop - lastScrollTop.current;
  //     const velocity = Math.abs(scrollDifference / timeElapsed); // pixels per millisecond

  //     // You can dispatch the velocity or use it as needed
  //     dispatch(taskActions.setScrollVelocity(velocity));
  //   }

  //   lastScrollTop.current = scrollTop;
  //   lastTimestamp.current = currentTimestamp;
  // };

  const idToIndexMap = useMemo(() => {
    return elementList.reduce<Record<string, number>>((acc, element, index) => {
      if (element.id) {
        acc[element.id] = index;
      }
      return acc;
    }, {});
  }, [elementList]);


  const getInitialIndex = (id: string | undefined) => {
    if (!id) return 0;
    const index = idToIndexMap[id];
    if (index !== undefined) {
      return index;
    } else return 0;

  };

  const initIndex = getInitialIndex(paramsTaskId);



  debugLog('task-selectors', `ChatTaskList rendering`);

  const [draggedItem, setDraggedItem] = useState<BoardRowElement>();

  const hasTaskResults = (elementList: BoardRowElement[], textFilter: string | undefined) => {
    void textFilter;
    const containsTypes = elementList.some(element =>
      element.type === 'task' || element.type === 'groupHeader'
    );

    if (!containsTypes && (textFilter || viewFilterLength > 0)) {
      return false;
    }
    return true;
  };

  const foundTasks = hasTaskResults(elementList, textFilter);



  const updateStickyHeader = useCallback(({ startIndex }: { startIndex: number; endIndex: number }) => {
    let newStickyHeader: BoardRowElement | null = null; // Default to null if no new header is found

    for (let i = startIndex; i >= 0; i--) {
      if (elementList[i].type === 'spacer' || elementList[i].type === 'newTask') {
        break; // Exit the loop as we're not updating for 'spacer' or 'newTask'
      }
      if (elementList[i].type === 'groupHeader') {
        newStickyHeader = elementList[i];
        break; // Found a groupHeader, so break the loop
      }
    }

    // Only update the stickyHeader state if the value has actually changed
    // This is checked by comparing the IDs (assuming each element has a unique ID)
    // Adjust the comparison condition based on how your elements are structured
    if (!stickyHeader || !newStickyHeader || stickyHeader.id !== newStickyHeader.id) {
      setStickyHeader(newStickyHeader);
    }
  }, [elementList, stickyHeader]); // Add stickyHeader to the dependency array


  const renderStickyHeader = () => {
    if (stickyHeader) {
      return (
        <div style={{
          position: "absolute", top: "0", zIndex: "10", transition: "ease-in 0.3s",
          background: "var(--tasks-group-bg)"
        }}>
          <GroupHeader groupId={stickyHeader.groupId ?? ''} provided={groupHeaderProvidedRef.current?.provided} />
        </div>
      );
    }
    return null;
  };

  const HeightPreservingItem = useCallback(
    ({
      children,
      ...props
    }: PropsWithChildren<
      DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
    >) => {
      return (
        <div
          {...props}
          className="height-preserving-container"
          //dir={direction}
          style={{ height: "40px", width: 'fit-content' }}
        >
          {children}
        </div>
      );
    },
    []
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {

      const [_, itemType, index] = result.draggableId.split('-');

      if (!result.destination) {
        return;
      }

      const sourceEl = draggedItem;
      setDraggedItem(undefined);
      const destinationEl = elementList[result.destination.index];

      let source: BTdragElement;
      let destination: BTdragElement;
      let entity = "";
      let dragThresholdFromIndex = 0;

      if (itemType === BoardRowType.groupHeader) {
        source = {
          _id: sourceEl?.groupId ?? '',
          index: result.source.index,
        }
        destination = {
          _id: destinationEl?.groupId ?? '',
          index: result.destination.index,
          type: destinationEl?.type,
        }
        entity = "groups";
        dragThresholdFromIndex = 0;

      } else {
        source = {
          _id: sourceEl?.taskId ?? '',
          index: result.source.index,
        }
        destination = {
          _id: destinationEl?.taskId ?? '',
          index: result.destination.index,
          parent: destinationEl?.parentTaskId, // task is parent of subtask
          grandparent: destinationEl?.groupId, // group is grandparent of task
          type: destinationEl?.type,
          IgnoreElementTypesOnBottomTop: [BoardRowType.spacer, BoardRowType.groupHeader],
          IgnoreElementTypesOnTopBottom: [BoardRowType.spacer, BoardRowType.newTask],
        }
        entity = "tasks";
      }



      dispatch(updateTasksOrderAfterTaskDragEnd({ source, destination, entity, dragThresholdFromIndex }));

      if (itemType === BoardRowType.groupHeader) {
        dispatch(taskActions.setIsGroupDragging(false))
      }


      dispatch(taskActions.setCurrentDraggedId(''))

    },
    [dispatch, draggedItem, elementList]
  );


  const onBeforeDragStart = (initial: DragStart) => {
    setDraggedItem(elementList[initial.source.index])
    dispatch(taskActions.setCurrentDraggedId(elementList[initial.source.index].id))
  }

  //handle sort if sort is pending save or pending reorder
  function handleDragStart(initial: DragStart, provided: ResponderProvided) {
    const [_, itemType, index] = initial.draggableId.split('-')

    if (itemType === BoardRowType.groupHeader) {
      dispatch(taskActions.setIsGroupDragging(true))
    }
  }

  // Mobile: render the stacked Monday.com-style card list instead of the wide
  // desktop table. Same elementList data; no DnD (poor on touch). The desktop
  // JSX below is left untouched.
  if (isMobile) {
    return (
      <>
        {!isTasksLoaded && <BoardsLoader />}
        {isTasksLoaded && (
          foundTasks ? (
            <MobileBoardView isDisplayNewGroupButton={isDisplayNewGroupButton} />
          ) : (
            <Stack sx={{ height: "50vh" }} justifyContent={"center"} alignItems={"center"}>
              <Lottie autoPlay={true} loop={true} animationData={NoResults} style={{ width: '300px', height: '300px' }} />
              <Box fontSize="18px" textAlign={"center"} sx={{ px: 2 }}>
                We've looked everywhere but couldn't find any matching tasks or items.
              </Box>
            </Stack>
          )
        )}
      </>
    );
  }

  // Campaign audience editor: while the board's data loads, render a row
  // skeleton IN PLACE — same container/direction/left-offset as the real rows,
  // so it aligns with where the table renders instead of a parent-positioned
  // placeholder. (The Tasks app keeps its BoardsLoader — only the campaign
  // context passes isLoading.)
  const showCampaignSkeleton = isLoading && isFromCampaign;

  return (
    <>
      {showCampaignSkeleton && (() => {
        // Shimmer cell — mirrors a real contact row (checkbox · avatar · name ·
        // column blocks) so the loader occupies the same footprint as the table
        // (rows start at the panel's left edge and stop ~120px short of the
        // right, not full-bleed). Theme-independent gray so it shows in the lean
        // campaign theme; the keyframe (campaigns-rd-shimmer) is global.
        const cell = (w: number | string, h: number, r: number | string = 6): React.CSSProperties => ({
          width: w, height: h, borderRadius: r, flexShrink: 0,
          background: "linear-gradient(90deg, rgba(0,0,0,.05) 25%, rgba(0,0,0,.10) 37%, rgba(0,0,0,.05) 63%)",
          backgroundSize: "900px 100%",
          animation: "campaigns-rd-shimmer 1.5s infinite linear",
        });
        return (
          <div dir={direction} style={{ paddingTop: 4 }} aria-busy="true" aria-label="Loading contacts">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                style={{
                  display: "flex", alignItems: "center", gap: 16,
                  height: 40, maxWidth: 1085, padding: "0 16px",
                  borderBottom: "1px solid rgba(0,0,0,.04)",
                  opacity: 1 - i * 0.09,
                }}
              >
                <div style={cell(16, 16, 4)} />
                <div style={cell(28, 28, "50%")} />
                <div style={cell(140, 12)} />
                <div style={{ flex: 1 }} />
                <div style={cell(150, 12)} />
                <div style={cell(90, 12)} />
                <div style={cell(90, 12)} />
              </div>
            ))}
          </div>
        );
      })()}
      {!showCampaignSkeleton && !isTasksLoaded &&
        <BoardsLoader />
      }
      {!showCampaignSkeleton && isTasksLoaded && (
        <div
          dir={direction}
          style={{
            // backgroundColor: theme?.boards?.tasksPanel?.background,
            position: "relative",
            // contain: "layout paint style",
          }}
        >
          {/* {renderStickyHeader()} */}

          {foundTasks ? (
            <DragDropContext
              onDragEnd={onDragEnd}
              onBeforeDragStart={onBeforeDragStart}
              onDragStart={handleDragStart}
            >
              <Droppable
                droppableId="board-list-droppable"
                mode="virtual"
                renderClone={(provided, snapshot) => (
                  draggedItem ? <BoardRow
                    isDisplayNewGroupButton={isDisplayNewGroupButton}
                    provided={provided}
                    isDragging={snapshot.isDragging}
                    item={draggedItem}
                    isClone
                  /> : <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} />
                )}
              >
                {(provided) => (
                  <Virtuoso
                    overscan={200}
                    components={{ Item: HeightPreservingItem }}
                    isScrolling={(isScrolling) => {
                      //console.log("isScrolling", isScrolling);
                      debouncedStopScrollDispatch(isScrolling);
                      debouncedStartScrollDispatch(isScrolling);

                    }}
                    initialTopMostItemIndex={initIndex}
                    scrollerRef={(ref: Window | HTMLElement | null) => {
                      if (!(ref instanceof HTMLElement)) return;
                      // Ensure Virtuoso's internal use of the ref is not disrupted
                      provided.innerRef(ref);

                      // Store the ref in your own useRef hook for additional uses
                      // if (ref) {
                      //   ref.addEventListener('scroll', () => {
                      //     setScrollVelocity(ref.scrollTop);
                      //   });
                      // }

                      (scrollContainerRef as React.MutableRefObject<HTMLElement | null>).current = ref;
                    }}
                    data={elementList}
                    rangeChanged={updateStickyHeader}
                    style={{
                      height: isExtensionContextOrWhatsappView()
                        ? "calc(100vh - 275px)"
                        : "calc(100vh - 201px)", // add -69px for tabs
                    }}
                    className={classes.virtuoso_wrapper}
                    itemContent={(index, item) => {
                      let draggableId = `draggable-${item.type}-${item.id}`;
                      if (item.type === BoardRowType.spacer) {
                        draggableId = `draggable-${item.type}-${index}`;
                      }

                      // Important, key and draggableId props must be equal!
                      return (
                        <Draggable
                          key={draggableId}
                          draggableId={draggableId}
                          index={index}
                          isDragDisabled={[
                            BoardRowType.task,
                            BoardRowType.subTask,
                            BoardRowType.groupHeader,
                          ].indexOf(item.type) === -1}
                        >
                          {(provided) => (
                            <BoardRow
                              item={item}
                              provided={provided}
                              isDragging={false}
                              isDisplayNewGroupButton={isDisplayNewGroupButton}
                            />
                          )}
                        </Draggable>
                      );
                    }}
                  />
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <Stack sx={{ height: "50vh" }} justifyContent={"center"} alignItems={"center"}>
              <Lottie autoPlay={true} loop={true} animationData={NoResults} style={{ width: '400px', height: '400px' }} />
              <Box fontSize="24px" textAlign={"center"}>
                We've looked everywhere but couldn't find any matching tasks or items.
                <br />
                Perhaps they're playing a game of hide-and-seek?
                <br /> Try refining your search or adjusting your filters to uncover them.
              </Box>
            </Stack>
          )}

          <ClickAwayListener onClickAway={() => {}}>
            <Popper
              open={isMoveTo && menuAnchorEl !== null}
              anchorEl={menuAnchorEl}
              placement="bottom-start"
              modifiers={[
                { name: 'offset', options: { offset: [0, 8] } },
                { name: 'preventOverflow', options: { padding: 8 } },
              ]}
              style={{ zIndex: 1300 }}
              container={
                portalContainer instanceof ShadowRoot
                  ? (portalContainer.host as HTMLElement)
                  : (portalContainer as HTMLElement | undefined)
              }
            >
              <MoveToMenu />
            </Popper>
          </ClickAwayListener>

          <ConditionalWrapper shouldLoadSelector={hasBulkTasks}>
            <BulkActionsDialog />
          </ConditionalWrapper>
          {process.env.NODE_ENV === 'development' && <FpsTracker />}
        </div>
      )}
    </>
  );
}

export default memo(ChatTaskList);
